/**
 * Pure grouping decisions for the soil grouping builder (U2).
 *
 * No React, no network, no i18n — every function here is a data transform, which
 * is why the logic lives in its own module: it is the part worth testing
 * exhaustively, and the page above it stays presentational.
 *
 * Labels (not uids) are returned by findBlockers because the page renders them
 * straight into a notice; uids are internal and meaningless to a user.
 */
import { SoilSessionFact } from '../../services/soilSessionService';

export interface DraftGroup {
  uid: string;
  label: string;
  orchardNames: string[];
  operators: { operatorId: string; name: string }[];
}

export interface OrchardRow {
  orchardName: string;
  cultivar: string;
  treeCount: number;
  areaHa: number;
  kg: number;
  groupUid: string | null;
  groupLabel: string | null;
}

export interface GroupTotals {
  kg: number;
  treeCount: number;
  areaHa: number;
  orchardCount: number;
  cultivars: string[];
}

export interface GroupingBlockers {
  noGroups: boolean;
  emptyGroupLabels: string[];
  noOperatorGroupLabels: string[];
  tooManyOperatorGroupLabels: string[];
}

const factMap = (facts: SoilSessionFact[]): Record<string, SoilSessionFact> => {
  const m: Record<string, SoilSessionFact> = {};
  facts.forEach((f) => { m[f.orchardName] = f; });
  return m;
};

/**
 * One row per orchard in the SESSION — not per fact. An orchard with no matching
 * fact still gets a zeroed row: dropping it would make it impossible to group and
 * invisible to the user, while the backend would still expect it accounted for.
 */
export function buildRows(orchards: string[], facts: SoilSessionFact[], groups: DraftGroup[]): OrchardRow[] {
  const byName = factMap(facts);
  const owner: Record<string, DraftGroup> = {};
  groups.forEach((g) => g.orchardNames.forEach((n) => { owner[n] = g; }));
  return orchards.map((orchardName) => {
    const f = byName[orchardName];
    const g = owner[orchardName];
    return {
      orchardName,
      cultivar: f?.cultivar || '',
      treeCount: f?.treeCount ?? 0,
      areaHa: f?.areaHa ?? 0,
      kg: f?.kg ?? 0,
      groupUid: g ? g.uid : null,
      groupLabel: g ? g.label : null,
    };
  });
}

export function groupTotals(group: DraftGroup, facts: SoilSessionFact[]): GroupTotals {
  const byName = factMap(facts);
  const picked = group.orchardNames.map((n) => byName[n]).filter(Boolean);
  return {
    kg: picked.reduce((s, f) => s + (f.kg || 0), 0),
    treeCount: picked.reduce((s, f) => s + (f.treeCount || 0), 0),
    areaHa: picked.reduce((s, f) => s + (f.areaHa || 0), 0),
    orchardCount: group.orchardNames.length,
    cultivars: Array.from(new Set(picked.map((f) => f.cultivar).filter(Boolean))),
  };
}

/**
 * Every unmet confirm condition, by group label. Mirrors what the backend's
 * assertPlannable enforces (NO_GROUPS / BAD_OPERATORS) plus the 4-operator cap
 * saveGroups enforces (TOO_MANY_OPERATORS), so the UI can name the problem
 * instead of silently disabling the button.
 */
export function findBlockers(groups: DraftGroup[]): GroupingBlockers {
  return {
    noGroups: groups.length === 0,
    emptyGroupLabels: groups.filter((g) => g.orchardNames.length === 0).map((g) => g.label),
    noOperatorGroupLabels: groups.filter((g) => g.operators.length === 0).map((g) => g.label),
    tooManyOperatorGroupLabels: groups.filter((g) => g.operators.length > 4).map((g) => g.label),
  };
}

export function canConfirm(b: GroupingBlockers): boolean {
  return !b.noGroups
    && b.emptyGroupLabels.length === 0
    && b.noOperatorGroupLabels.length === 0
    && b.tooManyOperatorGroupLabels.length === 0;
}

/** Assign orchards to a group, removing them from any group that held them. */
export function assignToGroup(groups: DraftGroup[], orchardNames: string[], targetUid: string): DraftGroup[] {
  const moving = new Set(orchardNames);
  return groups.map((g) => {
    const kept = g.orchardNames.filter((n) => !moving.has(n));
    if (g.uid !== targetUid) return { ...g, orchardNames: kept };
    const added = orchardNames.filter((n) => !kept.includes(n));
    return { ...g, orchardNames: [...kept, ...added] };
  });
}

export function unassign(groups: DraftGroup[], orchardName: string): DraftGroup[] {
  return groups.map((g) => ({ ...g, orchardNames: g.orchardNames.filter((n) => n !== orchardName) }));
}

export function removeGroup(groups: DraftGroup[], uid: string): DraftGroup[] {
  return groups.filter((g) => g.uid !== uid);
}

/**
 * Greedy fill: unassigned orchards, largest kg first, each into the currently
 * lightest group. Deliberately NOT a constraint solver — it is a starting point
 * the user then adjusts. Never moves an already-assigned orchard.
 */
export function autoBalance(groups: DraftGroup[], rows: OrchardRow[], facts: SoilSessionFact[]): DraftGroup[] {
  if (!groups.length) return groups;
  const byName = factMap(facts);
  const pending = rows
    .filter((r) => r.groupUid === null)
    .slice()
    .sort((a, b) => b.kg - a.kg || a.orchardName.localeCompare(b.orchardName));
  const working = groups.map((g) => ({ ...g, orchardNames: [...g.orchardNames] }));
  const load: Record<string, number> = {};
  working.forEach((g) => {
    load[g.uid] = g.orchardNames.reduce((s, n) => s + (byName[n]?.kg || 0), 0);
  });
  pending.forEach((row) => {
    let lightest = working[0];
    working.forEach((g) => { if (load[g.uid] < load[lightest.uid]) lightest = g; });
    lightest.orchardNames.push(row.orchardName);
    load[lightest.uid] += row.kg;
  });
  return working;
}

// Moved to utils/rangeSelect so soil and foliar share ONE definition.
// Re-exported here because soilGrouping.test.ts and SoilGroupingPage both
// import it by name from this module.
export { rangeSelect } from '../../utils/rangeSelect';

export function unassignedNames(orchards: string[], groups: DraftGroup[]): string[] {
  const assigned = new Set(groups.flatMap((g) => g.orchardNames));
  return orchards.filter((n) => !assigned.has(n));
}
